import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, MoreHorizontal, Plus } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { apiErrorMessage, apiFetch, v1Path } from '../../lib/api';
import { cn } from '../../lib/utils';
import {
  COLOR_RE,
  isStateDto,
  isStateGroup,
  parseItems,
  STATE_GROUPS,
  type StateDto,
  type StateGroup,
} from '../../lib/work-types';
import { ColorField, ColorSwatch, DEFAULT_COLOR } from './color-field';
import { useOverlays } from './overlay-context';
import { BrandEmpty, ErrorAlert, LoadingRows, PageTitle, WorkPageError } from './page-states';

const GROUP_LABELS: Record<StateGroup, string> = {
  backlog: 'Backlog',
  unstarted: 'Unstarted',
  started: 'Started',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function groupLabel(group: string): string {
  return isStateGroup(group) ? GROUP_LABELS[group] : group;
}

type StateForm = { name: string; group: StateGroup; color: string };

const EMPTY_FORM: StateForm = { name: '', group: 'backlog', color: DEFAULT_COLOR };

function neighboursAfterMove(ordered: StateDto[], movedId: string): {
  before_id: string | null;
  after_id: string | null;
} {
  const index = ordered.findIndex((state) => state.id === movedId);
  if (index < 0) return { before_id: null, after_id: null };
  return {
    before_id: ordered[index - 1]?.id ?? null,
    after_id: ordered[index + 1]?.id ?? null,
  };
}

function SortableStateRow({
  state,
  canManage,
  actingId,
  onEdit,
  onSetDefault,
  onDelete,
}: {
  state: StateDto;
  canManage: boolean;
  actingId: string | null;
  onEdit: (state: StateDto) => void;
  onSetDefault: (state: StateDto) => void;
  onDelete: (state: StateDto) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: state.id,
    disabled: !canManage || actingId === state.id,
  });

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={cn(isDragging && 'relative z-10 bg-muted opacity-70')}
    >
      {canManage ? (
        <TableCell className="w-10">
          <button
            type="button"
            className="flex h-8 w-8 cursor-grab items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={`Reorder ${state.name}`}
            disabled={actingId === state.id}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </TableCell>
      ) : null}
      <TableCell>
        <span className="flex items-center gap-2 text-sm">
          <ColorSwatch color={state.color} />
          {state.name}
        </span>
      </TableCell>
      <TableCell>
        <Badge variant="secondary">{groupLabel(state.group)}</Badge>
      </TableCell>
      <TableCell>
        {state.is_default ? <Badge>Default</Badge> : null}
      </TableCell>
      {canManage ? (
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={actingId === state.id}
                aria-label={`Actions for ${state.name}`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onEdit(state)}>Edit</DropdownMenuItem>
              {!state.is_default ? (
                <DropdownMenuItem onSelect={() => onSetDefault(state)}>
                  Set as default
                </DropdownMenuItem>
              ) : null}
              {!state.is_default ? (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => onDelete(state)}
                >
                  Delete
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      ) : null}
    </TableRow>
  );
}

export default function ProjectStatesSettings({
  projectId,
  canManage,
}: {
  projectId: string;
  canManage: boolean;
}) {
  const { toast, confirm } = useOverlays();
  const [states, setStates] = useState<StateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<StateDto | null>(null);
  const [form, setForm] = useState<StateForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);

  const statesPath = v1Path(`/projects/${projectId}/states`);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await apiFetch<unknown>(statesPath);
      setStates(parseItems(res, isStateDto, 'states'));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load states'));
      if (!opts?.silent) setStates([]);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [statesPath]);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(state: StateDto) {
    setEditing(state);
    setForm({
      name: state.name,
      group: isStateGroup(state.group) ? state.group : 'backlog',
      color: state.color,
    });
    setFormError(null);
    setDialogOpen(true);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) {
      setFormError('Name is required.');
      return;
    }
    if (!COLOR_RE.test(form.color)) {
      setFormError('Colour must be a hex value like #60646C.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const body = JSON.stringify({ name, group: form.group, color: form.color });
      if (editing) {
        await apiFetch(`${statesPath}/${editing.id}`, { method: 'PATCH', body });
        toast('State updated');
      } else {
        await apiFetch(statesPath, { method: 'POST', body });
        toast('State created');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setFormError(apiErrorMessage(err, 'Failed to save state'));
    } finally {
      setSaving(false);
    }
  }

  async function onSetDefault(state: StateDto) {
    setActingId(state.id);
    setError(null);
    try {
      await apiFetch(`${statesPath}/${state.id}/default`, { method: 'POST' });
      toast(`${state.name} is now the default state`);
      await load({ silent: true });
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to set default state');
      setError(message);
      toast(message);
    } finally {
      setActingId(null);
    }
  }

  async function onDragEnd(event: DragEndEvent) {
    if (!canManage) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = states.findIndex((state) => state.id === active.id);
    const newIndex = states.findIndex((state) => state.id === over.id);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

    const snapshot = states;
    const next = arrayMove(states, oldIndex, newIndex);
    const movedId = String(active.id);
    const { before_id, after_id } = neighboursAfterMove(next, movedId);
    setStates(next);
    setActingId(movedId);
    setError(null);
    try {
      await apiFetch(`${statesPath}/${movedId}/reorder`, {
        method: 'POST',
        body: JSON.stringify({ before_id, after_id }),
      });
      toast('State order updated');
      await load({ silent: true });
    } catch (err) {
      setStates(snapshot);
      const message = apiErrorMessage(err, 'Failed to reorder states');
      setError(message);
      toast(message);
    } finally {
      setActingId(null);
    }
  }

  async function onDelete(state: StateDto) {
    const ok = await confirm({
      title: 'Delete state',
      description: `Delete ${state.name}? This only works when no work items are in it.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setActingId(state.id);
    setError(null);
    try {
      await apiFetch(`${statesPath}/${state.id}`, { method: 'DELETE' });
      toast('State deleted');
      await load({ silent: true });
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to delete state');
      setError(message);
      toast(message);
    } finally {
      setActingId(null);
    }
  }

  const newStateButton = (
    <Button type="button" size="sm" onClick={openCreate}>
      <Plus className="mr-1.5 h-4 w-4" />
      New state
    </Button>
  );

  return (
    <div className="flex flex-1 flex-col">
      <PageTitle
        title="States"
        count={states.length}
        action={canManage ? newStateButton : null}
      />
      <p className="mb-4 text-sm text-muted-foreground">
        States are the columns on the board.
        {canManage ? ' Drag the handle to change their order. ' : ' '}
        Each one belongs to a fixed group that drives behaviour such as completion and archiving.
      </p>
      <WorkPageError error={error} projectId={projectId} onJoined={() => void load()} />
      {loading ? (
        <LoadingRows columns={4} />
      ) : states.length === 0 ? (
        <BrandEmpty title="No states yet." />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => void onDragEnd(event)}
        >
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                {canManage ? (
                  <TableHead className="w-10">
                    <span className="sr-only">Order</span>
                  </TableHead>
                ) : null}
                <TableHead className="min-w-0">Name</TableHead>
                <TableHead className="w-40">Group</TableHead>
                <TableHead className="w-28">Default</TableHead>
                {canManage ? <TableHead className="w-20 text-right">Actions</TableHead> : null}
              </TableRow>
            </TableHeader>
            <SortableContext
              items={states.map((state) => state.id)}
              strategy={verticalListSortingStrategy}
            >
              <TableBody>
                {states.map((state) => (
                  <SortableStateRow
                    key={state.id}
                    state={state}
                    canManage={canManage}
                    actingId={actingId}
                    onEdit={openEdit}
                    onSetDefault={(row) => void onSetDefault(row)}
                    onDelete={(row) => void onDelete(row)}
                  />
                ))}
              </TableBody>
            </SortableContext>
          </Table>
        </DndContext>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit state' : 'New state'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Change how this column is named, grouped, and coloured.'
                : 'New states are added to the end of the board.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            {formError ? <ErrorAlert>{formError}</ErrorAlert> : null}
            <div className="grid gap-2">
              <Label htmlFor="state-name">Name</Label>
              <Input
                id="state-name"
                value={form.name}
                maxLength={255}
                onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="state-group">Group</Label>
              <Select
                value={form.group}
                onValueChange={(value) => {
                  if (isStateGroup(value)) setForm((f) => ({ ...f, group: value }));
                }}
              >
                <SelectTrigger id="state-group">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATE_GROUPS.map((group) => (
                    <SelectItem key={group} value={group}>
                      {GROUP_LABELS[group]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Work items can only be archived from the Completed and Cancelled groups.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="state-color">Colour</Label>
              <ColorField
                id="state-color"
                value={form.color}
                onChange={(color) => setForm((f) => ({ ...f, color }))}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Save state' : 'Create state'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
